import { Component, Inject, Input, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { CultureService } from 'src/app/shared/culture/culture.service';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StoreService } from '../../shared/services/store.service';
import { UserService } from '../../shared/services/user.service';
import { NavComponent } from './components/nav/nav.component';
import { BRAND } from 'src/app/shared/brand';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnDestroy {
    /// The brand, for the template (shared/brand.ts).
    readonly brand = BRAND;

    @Input() layout: 'classic'|'compact' = 'classic';

    /// The owner's and a seller's account pages show no topbar and no logo-and-search row: the nav band carries the logo
    /// instead. Shoppers, and everyone outside /account, keep the full header. Decided in the browser only - the server
    /// render has no session. The two rows stay in the page and are hidden by a class on <html> (staff-view,
    /// _site-header.scss), which index.html already sets before the first paint from the hint kept here (STAFF_HINT), so a
    /// server-drawn account page does not jump when the app starts.
    staff = false;

    @ViewChild(NavComponent) nav: NavComponent;

    private destroy$ = new Subject<void>();

    constructor(
        public store: StoreService,
        private router: Router,
        private culture: CultureService,
        private user: UserService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) platformId: any,
    ) {
        if (isPlatformBrowser(platformId)) {
            merge(router.events.pipe(filter(e => e instanceof NavigationEnd)), user.onUserInfoChanged$)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.decide());
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /// Owner or seller, as the account menu decides it (layout.component.ts): a seller closed for good is a shopper again.
    private decide(): void {
        // In every language: /bn/account is the account area too.
        const path = this.culture.bare(this.router.url).split(/[?#]/)[0];
        const inAccount = path === '/account' || path.startsWith('/account/');
        const owner = this.user.isNijera() === true;
        const seller = this.user.userType() === this.user.userTypeConst.shop && this.user.sellerId() > 0;
        const staff = inAccount && (owner || seller);
        try {
            if (owner || seller) { localStorage.setItem(STAFF_HINT, '1'); } else { localStorage.removeItem(STAFF_HINT); }
        } catch { }
        this.document.documentElement.classList.toggle('staff-view', staff);
        // The panel (the account layout, drawn as a dashboard for staff) hides the whole header and footer: a page without
        // it - the cart, the checkout, a sign-in page - brings them back, whatever index.html guessed before the app started.
        // Only once a page is open: before the first navigation ends the layout is not drawn yet.
        if (this.router.navigated && (!staff || !this.document.querySelector('app-layout'))) {
            this.document.documentElement.classList.remove('staff-app');
        }
        if (staff !== this.staff) {
            this.staff = staff;
            // The band's sticky breakpoints were measured under the other header.
            setTimeout(() => this.nav && this.nav.calcBreakpoints(), 0);
        }
    }
}

/// The signed-in account is the owner's or a seller's (localStorage): index.html hides the two rows on /account before
/// the app starts. Change both together.
const STAFF_HINT = 'staff_view';
